// /ambassadeurs — Deur-overzicht van ALLE ambassadeurs (los van tickets).
// Nodig omdat sommige ambassadeurs alleen het €150-pakket kochten en GEEN toegangsticket:
// die staan niet op de ticket-deurlijst, maar moeten wel een speldje krijgen aan de deur.

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { listAmbassadors, setAmbassadorPinStatus, type AmbassadorRow } from "@/lib/api";

function sortByName(rows: AmbassadorRow[]): AmbassadorRow[] {
  return [...rows].sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl"));
}

function pinLabel(status: string): string {
  if (status === "delivered") return "✓ Uitgereikt";
  if (status === "sent") return "Verstuurd";
  return "Nog niet";
}

function htmlEscape(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}

// Print-vriendelijke speldjeslijst — alfabetisch, met afvink-vakje.
function printPinList(rows: AmbassadorRow[]) {
  const sorted = sortByName(rows);
  const total = sorted.length;
  const walkin = sorted.filter((a) => (a.own_ticket_count ?? 0) === 0).length;
  const delivered = sorted.filter((a) => a.pin_status === "delivered").length;
  const generated = dayjs().format("DD-MM-YYYY HH:mm");

  const body = sorted.map((a) => {
    const tc = a.own_ticket_count ?? 0;
    const ticket = tc > 0
      ? `${tc} ticket${tc === 1 ? "" : "s"}`
      : `<span class="walkin">GEEN TICKET — aan deur</span>`;
    const contact = [a.email, a.phone].filter(Boolean).map(htmlEscape).join(" · ");
    return `<tr>
      <td class="chk"></td>
      <td class="naam">${htmlEscape(a.name)}</td>
      <td>${ticket}</td>
      <td class="small">${contact}</td>
      <td class="chk pin"></td>
    </tr>`;
  }).join("");

  const doc = `<!DOCTYPE html>
<html lang="nl"><head><meta charset="utf-8"><title>Ambassadeurs — speldjeslijst</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #111; margin: 0; padding: 16px 18px; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .meta { font-size: 11px; color: #555; margin-bottom: 4px; }
  .totals { font-size: 12px; margin: 6px 0 12px; }
  .totals b { font-size: 14px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #bbb; padding: 5px 7px; text-align: left; font-size: 12px; vertical-align: top; }
  th { background: #eee; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
  td.chk { width: 26px; text-align: center; }
  td.chk::before { content: ""; display: inline-block; width: 14px; height: 14px; border: 1.5px solid #333; border-radius: 2px; }
  td.pin { width: 80px; }
  td.naam { font-weight: 600; }
  td.small { font-size: 10px; color: #555; }
  .walkin { color: #b00; font-weight: 700; }
  .toolbar { margin-bottom: 12px; }
  .toolbar button { font-size: 13px; padding: 6px 14px; cursor: pointer; }
  @media print { .toolbar { display: none; } @page { size: A4; margin: 12mm; } th { background: #eee !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="toolbar"><button onclick="window.print()">🖨️ Printen</button></div>
  <h1>Ambassadeurs — speldjeslijst</h1>
  <div class="meta">Gegenereerd ${generated} · sorteer: naam (A→Z)</div>
  <div class="totals"><b>${total}</b> ambassadeurs · <b>${walkin}</b> zonder ticket (aan deur) · ${delivered} speldje al uitgereikt</div>
  <table>
    <thead><tr>
      <th>✓</th><th>Naam</th><th>Eigen ticket</th><th>Contact</th><th>Speldje</th>
    </tr></thead>
    <tbody>${body}</tbody>
  </table>
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) {
    alert("Pop-up geblokkeerd. Sta pop-ups toe voor deze site om de lijst te printen.");
    return;
  }
  w.document.open();
  w.document.write(doc);
  w.document.close();
}

export function AmbassadorsIndex() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const { data: ambassadors, isLoading } = useQuery({
    // Eigen key + omzet-loze variant: geen financiële velden in de payload (ook voor medewerkers).
    queryKey: ["ambassadors", "door"],
    queryFn: () => listAmbassadors({ includeRevenue: false }),
  });

  const pinMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "pending" | "delivered" }) =>
      setAmbassadorPinStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ambassadors"] }),
    onError: (e: Error) => alert(e.message),
  });

  const rows = useMemo(() => {
    const all = sortByName(ambassadors ?? []);
    if (!q.trim()) return all;
    const n = q.toLowerCase();
    return all.filter((a) =>
      a.name.toLowerCase().includes(n) ||
      (a.email ?? "").toLowerCase().includes(n) ||
      (a.discount_code?.code ?? "").toLowerCase().includes(n));
  }, [ambassadors, q]);

  const total = (ambassadors ?? []).length;
  const walkin = (ambassadors ?? []).filter((a) => (a.own_ticket_count ?? 0) === 0).length;
  const delivered = (ambassadors ?? []).filter((a) => a.pin_status === "delivered").length;

  return (
    <>
      <div className="kpis" style={{ marginBottom: "1rem" }}>
        <div className="kpi">
          <div className="l">Ambassadeurs</div>
          <div className="v">{total}</div>
          <div className="delta">Totaal met speldje</div>
        </div>
        <div className="kpi">
          <div className="l">Zonder ticket</div>
          <div className="v">{walkin}</div>
          <div className="delta">Komen aan de deur (geen scan)</div>
        </div>
        <div className="kpi">
          <div className="l">Speldje uitgereikt</div>
          <div className="v">{delivered} / {total}</div>
          <div className="delta">Afgevinkt bij de deur</div>
        </div>
      </div>

      <div className="topbar-actions" style={{ marginBottom: "1rem" }}>
        <input
          className="search"
          placeholder="🔍 Zoek naam, email, code..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button
          type="button"
          className="btn-admin sm"
          onClick={() => printPinList(ambassadors ?? [])}
          disabled={!ambassadors || ambassadors.length === 0}
          title="Print een afvinkbare speldjeslijst van alle ambassadeurs"
        >
          🖨️ Speldjeslijst ({total})
        </button>
      </div>

      <div className="panel">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Naam</th><th>Contact</th><th>Eigen ticket</th><th>AMB-code</th><th>Speldje</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="empty"><span className="spinner" /> Laden…</td></tr>}
              {rows.map((a) => {
                const tc = a.own_ticket_count ?? 0;
                const delivered = a.pin_status === "delivered";
                return (
                  <tr key={a.id}>
                    <td><strong>{a.name}</strong></td>
                    <td>
                      <small>{a.email}</small>
                      {a.phone && <><br /><small style={{ opacity: 0.6 }}>{a.phone}</small></>}
                    </td>
                    <td>
                      {tc > 0 ? (
                        <span className="pill-st paid">{tc}× ticket</span>
                      ) : (
                        <span className="pill-st void" title="Geen toegangsticket — komt aan de deur, wordt niet gescand">⚠ geen ticket</span>
                      )}
                    </td>
                    <td>
                      {a.discount_code ? (
                        <code style={{ color: "var(--c-orange)", fontFamily: "var(--f-mono)", fontSize: "0.78rem" }}>{a.discount_code.code}</code>
                      ) : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td>
                      <span className={`pill-st ${delivered ? "scanned" : "issued"}`}>{pinLabel(a.pin_status)}</span>
                    </td>
                    <td>
                      <div className="row-actions">
                        {delivered ? (
                          <button
                            type="button"
                            onClick={() => pinMut.mutate({ id: a.id, status: "pending" })}
                            disabled={pinMut.isPending}
                            title="Speldje toch niet uitgereikt — terugzetten"
                          >Ongedaan</button>
                        ) : (
                          <button
                            type="button"
                            className="btn-mini"
                            onClick={() => pinMut.mutate({ id: a.id, status: "delivered" })}
                            disabled={pinMut.isPending}
                          >📍 Speldje uitgereikt</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={6} className="empty">Geen ambassadeurs gevonden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
